import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchWithAuth, streamWithAuth } from "@/lib/api/client";
import { showToast } from "@/lib/toast";

// ============================================================================
// Types
// ============================================================================

export interface ChatSessionOut {
  id: string;
  titulo: string | null;
  created_at: string;
  updated_at: string;
}

export interface ChatSourceOut {
  chunk_id: string;
  document_id: string;
  filename: string;
  page?: number | null;
  score?: number | null;
  content_preview?: string | null;
  cited_text?: string | null;
  email_meta?: {
    sender_email?: string | null;
    date?: string | null;
    subject?: string | null;
  } | null;
}

export interface ChatMessageOut {
  id: string;
  role: "user" | "assistant";
  content: string;
  sequence?: number;
  sources?: ChatSourceOut[] | null;
  model?: string | null;
  created_at: string;
}

export interface ChatFilters {
  document_ids?: string[];
  email_sender?: string;
  date_from?: string;
  date_to?: string;
}

export interface ChatStreamCitation {
  chunk_id: string;
  document_id: string;
  filename: string;
  page?: number | null;
  cited_text?: string | null;
}

export interface ChatStreamDone {
  message_id: string;
  content: string;
  model?: string | null;
  usage?: { input_tokens?: number; output_tokens?: number } | null;
  langfuse_trace_id?: string | null;
}

/**
 * Envía feedback 👍/👎 con un comentario opcional. El texto se guarda siempre
 * como comentario; además, en un 👎, alimenta la memoria de correcciones.
 */
export async function submitChatFeedback(
  traceId: string,
  positive: boolean,
  text?: string
): Promise<void> {
  const t = text?.trim() || undefined;
  await fetchWithAuth(`/api/v1/chat/feedback`, {
    method: "POST",
    body: JSON.stringify({
      trace_id: traceId,
      positive,
      comment: t,
      correction: positive ? undefined : t,
    }),
  });
}

export interface ChatStreamCallbacks {
  onSources: (sources: ChatSourceOut[]) => void;
  onDelta: (text: string) => void;
  onCitation: (citation: ChatStreamCitation) => void;
  onDone: (done: ChatStreamDone) => void;
}

// Tolera respuesta como array plano o envuelta en {items}/{sessions} etc.
function asList<T>(data: unknown, key?: string): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    for (const k of [key, "items", "results", "messages"].filter(
      Boolean
    ) as string[]) {
      if (Array.isArray(obj[k])) return obj[k] as T[];
    }
  }
  return [];
}

// ============================================================================
// Query Keys
// ============================================================================

export const chatKeys = {
  all: ["chat"] as const,
  sessions: (projectId: string) =>
    [...chatKeys.all, "sessions", projectId] as const,
  messages: (sessionId: string) =>
    [...chatKeys.all, "messages", sessionId] as const,
};

// ============================================================================
// Hooks
// ============================================================================

export function useChatSessions(projectId: string) {
  return useQuery<ChatSessionOut[], Error>({
    queryKey: chatKeys.sessions(projectId),
    queryFn: async () =>
      asList<ChatSessionOut>(
        await fetchWithAuth(`/api/v1/projects/${projectId}/chat/sessions`),
        "sessions"
      ),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

export async function createChatSession(
  projectId: string,
  titulo?: string
): Promise<ChatSessionOut> {
  return fetchWithAuth(`/api/v1/projects/${projectId}/chat/sessions`, {
    method: "POST",
    body: JSON.stringify(titulo ? { titulo } : {}),
  });
}

export function useChatMessages(sessionId: string | undefined) {
  return useQuery<ChatMessageOut[], Error>({
    queryKey: chatKeys.messages(sessionId ?? ""),
    queryFn: async () =>
      asList<ChatMessageOut>(
        await fetchWithAuth(`/api/v1/chat/sessions/${sessionId}/messages`),
        "messages"
      ),
    enabled: !!sessionId,
    staleTime: 0,
  });
}

export function useDeleteChatSession(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (sessionId) =>
      fetchWithAuth(`/api/v1/chat/sessions/${sessionId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatKeys.sessions(projectId) });
      showToast.success("Conversación eliminada");
    },
    onError: (e) =>
      showToast.error(e.message || "Error al eliminar la conversación"),
  });
}

/**
 * Envía un mensaje y consume el stream SSE del backend.
 *
 * Eventos: 'sources' → lista de chunks recuperados; 'delta' → trozo de
 * texto; 'citation' → cita puntual con cited_text; 'done' → mensaje
 * persistido; 'error' → mensaje de error (lanza Error).
 *
 * EventSource no puede hacer POST con Authorization, así que esto va por
 * fetch + ReadableStream (streamWithAuth).
 */
export async function streamChatMessage(
  sessionId: string,
  body: { content: string; filters?: ChatFilters },
  callbacks: ChatStreamCallbacks,
  signal?: AbortSignal
): Promise<void> {
  let streamError: string | null = null;

  await streamWithAuth(
    `/api/v1/chat/sessions/${sessionId}/messages`,
    { method: "POST", body: JSON.stringify(body) },
    ({ event, data }) => {
      switch (event) {
        case "sources":
          callbacks.onSources((data as ChatSourceOut[]) ?? []);
          break;
        case "delta": {
          const text = (data as { text?: string })?.text;
          if (text) callbacks.onDelta(text);
          break;
        }
        case "citation":
          callbacks.onCitation(data as ChatStreamCitation);
          break;
        case "done":
          callbacks.onDone(data as ChatStreamDone);
          break;
        case "error":
          streamError =
            (data as { message?: string })?.message ??
            "Error generando la respuesta";
          break;
      }
    },
    signal
  );

  if (streamError) throw new Error(streamError);
}
